import React, { useEffect, useRef, useState } from "react";
import { useGetSearchStream, useGetChatHistory, useGetConversationId, useDebouncedScroll } from "hooks";
import { Layer, Body, Footer, Header, ChatForm, ChatResponseCard, IntroText, LoadingIndicator } from "./components";
import args from "./arguments";
import { HttpStatusCode } from "axios";

const App = () => {
  const {
    searchStreamData,
    searchStreamError,
    isSearchStreamLoading,
    fetchSearchStreamData,
    resetSearchStreamData,
    abortSearchStream
  } = useGetSearchStream()
  const {
    chatHistoryData,
    fetchChatHistory,
    addToChatHistory,
    isChatHistoryLoading,
    chatHistoryErrorData
  } = useGetChatHistory()
  const {
    conversationId,
    fetchCurrentConversationId,
    isConversationIdLoading,
    resetConversationId
  } = useGetConversationId()
  const layerRef = useRef<{ handleToggleOverlay: () => void }>(null);
  const chatAnswerRef = useRef<HTMLDivElement>(null)
  const [isNewChat, setIsNewChat] = useState<boolean>(false)
  const siteId = args.getArgument('siteId')

  useDebouncedScroll(chatAnswerRef, [
    searchStreamData.summary,
    isSearchStreamLoading,
    isChatHistoryLoading,
  ], 100);

  useEffect(() => {
    void fetchCurrentConversationId()
    return () => {
      abortSearchStream()
    }
  }, []);

  useEffect(() => {
    if (chatHistoryErrorData?.status && chatHistoryErrorData.status === HttpStatusCode.Forbidden) {
      resetConversationId()
    }
  }, [chatHistoryErrorData]);

  useEffect(() => {
    if (conversationId) {
      void fetchChatHistory(conversationId)
    }
  }, [conversationId]);

  useEffect(() => {
    if (!isSearchStreamLoading && searchStreamData) {
      addToChatHistory({
        question: searchStreamData.question,
        summary: searchStreamData.summary,
        sources: searchStreamData.sources || [],
        suggestions: searchStreamData.suggestions || [],
        siteId,
        conversationId,
        questionId: `${+new Date()}`,
        convergenceContent: searchStreamData.convergenceContent,
        star: 0
      })
      resetSearchStreamData()
    }
  }, [isSearchStreamLoading]);

  const onSubmitQuestion = (question: string) => {
    if (question && conversationId) {
      void fetchSearchStreamData(question, conversationId, siteId)
      setIsNewChat(false)
    }
  }

  const onNewChat = () => {
    resetConversationId()
    abortSearchStream()
    setIsNewChat(true)
  }

  const handleToggleOverlay = () => {
    layerRef.current?.handleToggleOverlay()
    abortSearchStream()
  }

  const isLoading = isChatHistoryLoading && isConversationIdLoading
  const showIntro = !searchStreamData.question && !chatHistoryData.length && !isLoading

  return (
    <Layer ref={layerRef}>
      <Header
        handleToggleOverlay={handleToggleOverlay}
        onNewChat={onNewChat}
      />
      <Body isNewChat={isNewChat}>
        {showIntro && (
          <IntroText
            isNewChat={isNewChat}
            handleClickSuggestion={onSubmitQuestion}
          />
        )}
        {isLoading && <LoadingIndicator/>}
        {chatHistoryData.map((item, key) => {
          const displayError = key === chatHistoryData.length - 1
          return <ChatResponseCard
            key={item.questionId}
            question={item.question}
            summary={item.summary}
            suggestions={item.suggestions}
            sources={item.sources}
            handleClickSuggestion={onSubmitQuestion}
            siteId={siteId}
            conversationId={conversationId}
            hasError={!!searchStreamError && displayError}
            errorMessage={displayError ? searchStreamError?.message : ''}
            stars={item.star}
            convergenceContent={item.convergenceContent}
          />
        })}

        <div ref={chatAnswerRef}>
          <ChatResponseCard
            question={searchStreamData.question}
            summary={searchStreamData?.summary}
            suggestions={searchStreamData?.suggestions}
            sources={searchStreamData?.sources}
            handleClickSuggestion={onSubmitQuestion}
            isLoading={isSearchStreamLoading}
            siteId={siteId}
            conversationId={conversationId}
            convergenceContent={searchStreamData.convergenceContent}
          />
        </div>
      </Body>
      <Footer>
        <ChatForm onSubmitQuestion={onSubmitQuestion} isDisabled={isSearchStreamLoading}/>
      </Footer>
    </Layer>
  )
}

export default App
